import React, { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import { FetchApi } from "../../../api/fetch"
import {
  CommunityCard,
  TokenCommunityCard,
} from "../../../components/CommunityCard"
import Button from "../../../components/Button"
import { BASE_URL_API_DE_NEW, GO_DE_GQL_BACKEND_URL } from "../../../constants"
import { useAuth } from "../../../contexts/AuthContext"
import { FeaturedProps } from "../../../types/featured"
import Modal from "../../../components/Modal"
import { ManageNFTPartner, ManageTokenPartner } from "./partner"

function WhitelistCommunities() {
  return (
    <div className="w-11/12 m-auto my-6">
      <div className="mt-0 mb-12 flex flex-row justify-between items-center sm:flex-row">
        <h1 className="font-semibold text-3xl text-left">
          Whitelist Partner Community
        </h1>
      </div>
      <NFTPartners />
      <TokenPartners />
    </div>
  )
}

export default WhitelistCommunities

const PAGE_SIZE = 6

function TokenPartners() {
  const { setIsLoadingOverlayDisplay } = useAuth()
  const [showPartnerModal, setShowPartnerModal] = useState(false)
  const [featured, setFeatured] = useState<FeaturedProps[]>([])
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const [selected, setSelected] = useState<FeaturedProps | undefined>()

  useEffect(() => {
    getCommunity()
  }, [])

  const getCommunity = async () => {
    setIsLoadingOverlayDisplay(true)

    try {
      const feat = await FetchApi({
        endpoint: GO_DE_GQL_BACKEND_URL,
        method: "POST",
        body: {
          query: `{
               whitelistPartner {
               nodes {
               updatedAt
               position
               numberOfSpots
               image
               id
               createdAt
               collectionName
               collectionId
            }
            }}`,
          variables: {},
        },
      })

      console.log("part", feat)

      const sorted = feat?.data?.tokenWhitelistPartner?.nodes?.sort(
        (a: any, b: any) => a.position - b.position
      )
      setFeatured(sorted)
    } catch (error) {
      console.error(error)
      toast.error("Failed to load partners")
    } finally {
      setIsLoadingOverlayDisplay(false)
    }
  }

  const saveFeatured = async (
    item: FeaturedProps,
    newPos: number,
    id: string
  ) => {
    const userJWT = JSON.parse(sessionStorage.getItem("userData") || "{}").token

    try {
      await FetchApi({
        endpoint: `${BASE_URL_API_DE_NEW}v2/user/update-whitelist-partner/${id}`,
        method: "PUT",
        JWT: userJWT,
        body: { ...item, position: newPos },
      })
    } catch (error) {
      console.error("Failed to save partner", error)
      toast.error("Something went wrong while saving")
    }
  }

  const onDragEnd = (result: any) => {
    if (!result.destination) return

    const newItems = [...featured]
    const [reordered] = newItems.splice(result.source.index, 1)
    newItems.splice(result.destination.index, 0, reordered)

    setFeatured(newItems)

    saveFeatured(reordered, result.destination.index + 1, reordered.id ?? "")
  }

  return (
    <div className="border border-kyzzen-secondary p-10 rounded-x10 mb-8">
      {/* Header */}
      <div className="mb-8 flex flex-row justify-between items-center sm:flex-row">
        <h1 className="font-semibold text-2xl">Token Partner Community</h1>
        <Button
          type="button"
          onClick={() => {
            setSelected(undefined)
            setShowPartnerModal(true)
          }}
          className="btn bg-gradient text-kyzzen-primary text-sm font-medium"
        >
          Add New
        </Button>
      </div>

      {/* Drag and Drop List */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="featured">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {featured?.slice(0, visibleCount).map((item, index) => (
                <Draggable
                  key={item.id}
                  draggableId={index.toString()}
                  index={index}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="mb-4"
                    >
                      <TokenCommunityCard
                        key={item.id}
                        data={item}
                        getCommunity={getCommunity}
                        initiateEdit={() => {
                          setSelected(item)
                          setShowPartnerModal(true)
                        }}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Show More Button */}
      {visibleCount < featured?.length && (
        <div className="mt-6 text-center">
          <Button
            className="btn bg-gradient text-kyzzen-primary text-sm font-medium"
            onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
          >
            Show More
          </Button>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showPartnerModal} hide={() => setShowPartnerModal(false)}>
        <ManageTokenPartner
          getCommunity={getCommunity}
          hide={() => setShowPartnerModal(false)}
          partner={selected}
        />
      </Modal>
    </div>
  )
}

function NFTPartners() {
  const { setIsLoadingOverlayDisplay } = useAuth()
  const [showPartnerModal, setShowPartnerModal] = useState(false)
  const [featured, setFeatured] = useState<FeaturedProps[]>([])
  const [selected, setSelected] = useState<FeaturedProps | undefined>()
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  useEffect(() => {
    getCommunity()
  }, [])

  const getCommunity = async () => {
    setIsLoadingOverlayDisplay(true)

    try {
      const res = await FetchApi({
        method: "POST",
        endpoint: GO_DE_GQL_BACKEND_URL,
        body: {
          query: `{
            whitelistPartner {
              nodes {
                id
                collectionId
                collectionName
                createdAt
                updatedAt
                image
                numberOfSpots
                position
                totalAllocations
              }
            }
          }`,
        },
      })

      const sorted = res?.data?.whitelistPartner?.nodes?.sort(
        (a: FeaturedProps, b: FeaturedProps) => a.position - b.position
      )
      setFeatured(sorted || [])
    } catch (error) {
      console.error(error)
      toast.error("Failed to load partners")
    } finally {
      setIsLoadingOverlayDisplay(false)
    }
  }

  const saveFeatured = async (
    item: FeaturedProps,
    newPos: number,
    id: string
  ) => {
    const userJWT = JSON.parse(sessionStorage.getItem("userData") || "{}").token

    try {
      let response: any = await FetchApi({
        endpoint: `${BASE_URL_API_DE_NEW}v2/user/update-whitelist-partner/${id}`,
        method: "PUT",
        JWT: userJWT,
        body: { ...item, position: newPos },
      })
      response = await response.json()

      if (!response.success) {
        toast.error(response.message)
      }
    } catch (error) {
      console.error("Failed to save partner", error)
      toast.error("Something went wrong while saving")
    }
  }

  const onDragEnd = (result: any) => {
    if (!result.destination) return

    const newItems = [...featured]
    const [reordered] = newItems.splice(result.source.index, 1)
    newItems.splice(result.destination.index, 0, reordered)

    setFeatured(newItems)

    saveFeatured(reordered, result.destination.index + 1, reordered.id ?? "")
  }

  return (
    <div className="border border-kyzzen-secondary p-10 rounded-x10 mb-8">
      {/* Header */}
      <div className="mb-8 flex flex-row justify-between items-center sm:flex-row">
        <h1 className="font-semibold text-2xl">NFT Partner Community</h1>
        <Button
          type="button"
          onClick={() => {
            setSelected(undefined)
            setShowPartnerModal(true)
          }}
          className="btn bg-gradient text-kyzzen-primary text-sm font-medium"
        >
          Add New
        </Button>
      </div>

      {/* Drag and Drop List */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="featured">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {featured?.slice(0, visibleCount).map((item, index) => (
                <Draggable
                  key={item.id}
                  draggableId={index.toString()}
                  index={index}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="mb-4"
                    >
                      <CommunityCard
                        key={item.id}
                        data={item}
                        getCommunity={getCommunity}
                        initiateEdit={() => {
                          setSelected(item)
                          setShowPartnerModal(true)
                        }}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Show More Button */}
      {visibleCount < featured?.length && (
        <div className="mt-6 text-center">
          <Button
            className="btn bg-gradient text-kyzzen-primary text-sm font-medium"
            onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
          >
            Show More
          </Button>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showPartnerModal} hide={() => setShowPartnerModal(false)}>
        <ManageNFTPartner
          getCommunity={getCommunity}
          hide={() => setShowPartnerModal(false)}
          partner={selected}
        />
      </Modal>
    </div>
  )
}
